import { useState, useEffect } from 'react';
import { Upload, Image, Save } from 'lucide-react';
import { chBrandingService } from '../services';
import { useModal } from '../../contexts/ModalContext';
import type { ChProject } from '../types';

interface Props {
  project: ChProject;
}

export function ChBrandingTab({ project }: Props) {
  const { toast } = useModal();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    primary_color: '#D6FF4B',
    background_color: '#15140F',
    accent_color: '',
    logo_url: '',
    background_image_url: '',
  });

  useEffect(() => {
    loadBranding();
  }, [project.id]);

  async function loadBranding() {
    try {
      setLoading(true);
      const data = await chBrandingService.get(project.id);
      if (data) {
        setForm({
          primary_color: data.primary_color || '#D6FF4B',
          background_color: data.background_color || '#15140F',
          accent_color: data.accent_color || '',
          logo_url: data.logo_url || '',
          background_image_url: data.background_image_url || '',
        });
      }
    } catch {
      toast.error('Erro ao carregar o branding');
    } finally {
      setLoading(false);
    }
  }

  async function handleLogoUpload(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (!file) return;
    try {
      const path = `${project.id}/logo-${Date.now()}.${file.name.split('.').pop()}`;
      const url = await chBrandingService.uploadFile('ch_branding', path, file);
      setForm(f => ({ ...f, logo_url: url }));
    } catch {
      toast.error('Erro ao carregar o logótipo');
    }
  }

  async function handleBgUpload(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (!file) return;
    try {
      const path = `${project.id}/bg-${Date.now()}.${file.name.split('.').pop()}`;
      const url = await chBrandingService.uploadFile('ch_branding', path, file);
      setForm(f => ({ ...f, background_image_url: url }));
    } catch {
      toast.error('Erro ao carregar a imagem de fundo');
    }
  }

  async function handleSave() {
    setSaving(true);
    try {
      await chBrandingService.upsert({
        project_id: project.id,
        primary_color: form.primary_color,
        background_color: form.background_color,
        accent_color: form.accent_color || null,
        logo_url: form.logo_url || null,
        background_image_url: form.background_image_url || null,
      });
      await loadBranding();
      toast.success('Branding guardado');
    } catch {
      toast.error('Erro ao guardar o branding');
    } finally {
      setSaving(false);
    }
  }

  if (loading) {
    return <div className="ch-card animate-pulse h-64" />;
  }

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 ch-animate-in">
      {/* Form */}
      <div className="ch-card space-y-5">
        <h3 className="font-semibold text-sm text-[var(--ch-ink)]">Configuração de Branding</h3>

        <div>
          <label className="ch-label">Logo</label>
          {form.logo_url ? (
            <div className="flex items-center gap-3">
              <img src={form.logo_url} alt="Logo" className="h-12 w-auto rounded-lg bg-gray-100 p-1" />
              <label className="ch-btn ch-btn-ghost ch-btn-sm cursor-pointer">
                <Upload className="w-3.5 h-3.5" />
                Alterar
                <input type="file" accept="image/*" className="hidden" onChange={handleLogoUpload} />
              </label>
            </div>
          ) : (
            <label className="flex items-center justify-center gap-2 p-4 border-2 border-dashed border-gray-200 rounded-xl cursor-pointer hover:border-gray-300 transition-colors">
              <Image className="w-5 h-5 text-gray-400" />
              <span className="text-sm text-gray-500">Carregar logo</span>
              <input type="file" accept="image/*" className="hidden" onChange={handleLogoUpload} />
            </label>
          )}
        </div>

        <div>
          <label className="ch-label">Imagem de Fundo</label>
          {form.background_image_url ? (
            <div className="relative">
              <img src={form.background_image_url} alt="Background" className="w-full h-24 object-cover rounded-xl" />
              <label className="absolute bottom-2 right-2 ch-btn ch-btn-ghost ch-btn-sm bg-white/80 backdrop-blur cursor-pointer">
                <Upload className="w-3.5 h-3.5" />
                Alterar
                <input type="file" accept="image/*" className="hidden" onChange={handleBgUpload} />
              </label>
            </div>
          ) : (
            <label className="flex items-center justify-center gap-2 p-4 border-2 border-dashed border-gray-200 rounded-xl cursor-pointer hover:border-gray-300 transition-colors">
              <Image className="w-5 h-5 text-gray-400" />
              <span className="text-sm text-gray-500">Carregar imagem de fundo (opcional)</span>
              <input type="file" accept="image/*" className="hidden" onChange={handleBgUpload} />
            </label>
          )}
        </div>

        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="ch-label">Cor Primária</label>
            <div className="flex items-center gap-2">
              <input
                type="color"
                value={form.primary_color}
                onChange={e => setForm(f => ({ ...f, primary_color: e.target.value }))}
                className="w-10 h-10 rounded-lg border border-gray-200 cursor-pointer"
              />
              <input
                type="text"
                className="ch-input flex-1 font-mono text-xs"
                value={form.primary_color}
                onChange={e => setForm(f => ({ ...f, primary_color: e.target.value }))}
              />
            </div>
          </div>
          <div>
            <label className="ch-label">Cor de Fundo</label>
            <div className="flex items-center gap-2">
              <input
                type="color"
                value={form.background_color}
                onChange={e => setForm(f => ({ ...f, background_color: e.target.value }))}
                className="w-10 h-10 rounded-lg border border-gray-200 cursor-pointer"
              />
              <input
                type="text"
                className="ch-input flex-1 font-mono text-xs"
                value={form.background_color}
                onChange={e => setForm(f => ({ ...f, background_color: e.target.value }))}
              />
            </div>
          </div>
        </div>

        <div>
          <label className="ch-label">Cor de Destaque (opcional)</label>
          <div className="flex items-center gap-2">
            <input
              type="color"
              value={form.accent_color || '#FF4FA3'}
              onChange={e => setForm(f => ({ ...f, accent_color: e.target.value }))}
              className="w-10 h-10 rounded-lg border border-gray-200 cursor-pointer"
            />
            <input
              type="text"
              className="ch-input flex-1 font-mono text-xs"
              value={form.accent_color}
              onChange={e => setForm(f => ({ ...f, accent_color: e.target.value }))}
              placeholder="#FF4FA3"
            />
          </div>
        </div>

        <button onClick={handleSave} disabled={saving} className="ch-btn ch-btn-primary w-full">
          <Save className="w-4 h-4" />
          {saving ? 'A guardar...' : 'Guardar Branding'}
        </button>
      </div>

      {/* Live preview */}
      <div className="ch-card p-0 overflow-hidden">
        <h3 className="font-semibold text-sm text-[var(--ch-ink)] p-4 pb-0">
          Pré-visualização (Vista do Cliente)
        </h3>
        <div
          className="m-4 rounded-2xl overflow-hidden min-h-[300px] flex flex-col items-center justify-center p-8"
          style={{
            backgroundColor: form.background_color,
            backgroundImage: form.background_image_url ? `url(${form.background_image_url})` : undefined,
            backgroundSize: 'cover',
            backgroundPosition: 'center',
          }}
        >
          {form.logo_url && (
            <img src={form.logo_url} alt="Logo" className="h-12 w-auto mb-6" />
          )}
          <div className="text-center">
            <h4 className="text-lg font-bold mb-2" style={{ color: form.primary_color }}>
              {project.name}
            </h4>
            <p className="text-sm opacity-60" style={{ color: form.primary_color }}>
              Revisão de conteúdo
            </p>
          </div>
          <div className="mt-6 flex gap-2">
            <span
              className="px-3 py-1.5 rounded-full text-xs font-bold"
              style={{ backgroundColor: form.primary_color, color: form.background_color }}
            >
              Aprovar
            </span>
            <span
              className="px-3 py-1.5 rounded-full text-xs font-bold border"
              style={{ borderColor: form.primary_color, color: form.primary_color }}
            >
              Pedir alterações
            </span>
          </div>
        </div>
      </div>
    </div>
  );
}
